import { CheckCircle2, ReceiptText } from "lucide-react";

import PaginationComponent from "../PaginationComponent";
import { Button } from "../../utils/components/ui/button";
import { PosSettlementSession } from "../../types/posSettlementTypes";

type PosSettlementTableProps = {
  rows: PosSettlementSession[];
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  totalElements: number;
  onPageChange: (page: number) => void;
  onSettle: (row: PosSettlementSession) => void;
  formatCurrency: (value: number) => string;
  formatDate: (value: string) => string;
};

function PosSettlementTable({
  rows,
  isLoading,
  currentPage,
  totalPages,
  totalElements,
  onPageChange,
  onSettle,
  formatCurrency,
  formatDate,
}: PosSettlementTableProps) {
  return (
    <>
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="min-w-[960px] w-full divide-y divide-[#EEF2F6]">
          <thead className="sticky top-0 z-10 bg-[#F5F8FD]">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Session ID
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Business Date
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Expected POS
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Actual POS
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Status
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EEF2F6] bg-white">
            {rows.length > 0 ? (
              rows.map((row) => (
                <tr key={row.sessionId} className="transition hover:bg-[#FAFCFF]">
                  <td className="px-6 py-5 text-sm font-semibold text-slate-900">
                    #{row.sessionId}
                  </td>
                  <td className="px-6 py-5 text-sm text-slate-600">
                    {formatDate(row.businessDate)}
                  </td>
                  <td className="px-6 py-5 text-right text-sm font-medium text-slate-900">
                    {formatCurrency(row.expectedPos)}
                  </td>
                  <td className="px-6 py-5 text-right text-sm font-medium text-slate-900">
                    {formatCurrency(row.actualPos)}
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                        row.isPosSettled
                          ? "border border-[#D9F2E5] bg-[#EFFAF4] text-[#0E9F6E]"
                          : "border border-[#FFE7C2] bg-[#FFF8EC] text-[#D97706]"
                      }`}
                    >
                      {row.isPosSettled ? (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      ) : (
                        <ReceiptText className="h-3.5 w-3.5" />
                      )}
                      {row.isPosSettled ? "Settled" : "Pending"}
                    </span>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <Button
                      type="button"
                      onClick={() => onSettle(row)}
                      disabled={row.isPosSettled}
                      className="h-10 rounded-xl bg-[#1A5EF9] px-4 text-white hover:bg-[#0E4FDE] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                    >
                      {row.isPosSettled ? "Completed" : "Settle"}
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-12 text-center text-sm text-slate-500"
                >
                  {isLoading
                    ? "Loading POS sessions..."
                    : "No POS sessions found for the selected filters."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 0 && (
        <div className="flex flex-col gap-4 border-t border-[#EEF2F6] bg-[linear-gradient(180deg,#FFFFFF_0%,#FBFDF8_100%)] px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-500">
            <span className="font-semibold text-slate-900">{totalElements}</span>{" "}
            sessions found
          </p>
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </>
  );
}

export default PosSettlementTable;
